import { ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'
import { ZStream } from '@mxt/zio/stream'
import {
  AmlResult,
  AmlService,
  assets,
  Button,
  DetailNav,
  DetailService,
  Employee,
  EmployeeC,
  HistoryType,
  RefreshButton,
  UtilRow,
  View
} from '@pulseops/business/core'
import { didUpdate, ErrorHandling, subscribe, Permission, RBAC } from '@pulseops/common'
import { pipe } from 'fp-ts/function'
import * as O from 'fp-ts/Option'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/native'
import { EditHistoryModalService } from '../../../edit-history-modal-service'
import { ContractDetailContext } from '../../contract-detail-context'
import { EmployeeDetailEdit } from './employee-detail-edit'
import { EmployeeDetailView } from './employee-detail-view'

type Props = {
  employeeKey: string
  onClose: () => void
}

export const EmployeeDetail = ({ employeeKey, onClose }: Props) => {
  const { t } = useTranslation('business')
  const roles: string[] = pipe(RBAC.permissions, ErrorHandling.runDidMount([]))

  const { contractId } = React.useContext(ContractDetailContext)

  const service = DetailService.contract(contractId)

  const [refreshLoading, bindRefreshLoading] = useLoading(false)
  const [amlLoading, bindAmlLoading] = useLoading(false)
  const [isEdit, setIsEdit] = React.useState(false)

  const detail: Employee | null = pipe(
    didUpdate([employeeKey]),
    ZStream.switchMap(([employeeKey]) => service.state.employee.item(employeeKey).watch),
    subscribe()
  )

  EditHistoryModalService.useHistory(service.state.history.clear)

  const history: Employee | null = pipe(
    EditHistoryModalService.viewing.stream,
    ZStream.chainEffect((item) =>
      pipe(
        service.state.employee.item(employeeKey).get,
        ZIO.map((detail) =>
          pipe(
            item,
            O.fromNullable,
            O.filter((item) => !!detail && item.version < detail.version),
            O.chain((item) => pipe(JSON.parse(JSON.stringify(item)), EmployeeC.decode, O.fromEither)),
            O.toNullable
          )
        )
      )
    ),
    subscribe()
  )

  const refresh = () => {
    pipe(service.refreshEmployee(employeeKey), bindRefreshLoading, ErrorHandling.run())
  }

  const scanAml = () => {
    if (!detail) {
      return
    }
    pipe(
      AmlService.scanEmployee({
        uuid: detail.uuid,
        dateOfBirth: new Date(detail.dateOfBirth).getTime(),
        employeeId: detail.employeeId,
        employeeName: detail.employeeName,
        gender: detail.gender,
        nationality: detail.nationality,
        occupation: detail.occupation,
        permanentAddress: detail.permanentAddress,
        permanentAddressCountry: detail.permanentAddressCountry,
        residentialAddress: detail.residentialAddress,
        residentialAddressCountry: detail.residentialAddressCountry,
        employeeNo: detail.employeeNo,
        uniqueIdentifierAml: detail?.uniqueIdentifierAml ?? '',
        amlResult: AmlResult.formatLatest(detail.historyAmlResult).result,
        clientId: detail.employeeId
      }),
      bindAmlLoading,
      ErrorHandling.run()
    )
  }

  if (!detail) {
    return null
  }

  return (
    <>
      <UtilRow>
        <Button
          title={t('edit_history')}
          type="outline"
          icon={<assets.IconTime />}
          onPress={() => {
            EditHistoryModalService.viewHistory({
              historyType: HistoryType.Employee,
              masterContractNo: contractId.masterContractNo,
              policyYear: contractId.policyYear.toString(),
              employeeKey
            })
          }}
          disabled={isEdit || refreshLoading || amlLoading}
        />
        {roles.includes(Permission['EditEmployeeGroupDetailGeneralInfo']) && (
          <Button
            title={t('edit')}
            type="outline"
            icon={<assets.IconEdit />}
            onPress={() => {
              setIsEdit(true)
            }}
            disabled={isEdit || refreshLoading || amlLoading || !!history}
          />
        )}
        <Button
          title={t('update_aml')}
          type="outline"
          icon={<assets.IconPaper />}
          onPress={scanAml}
          loading={amlLoading}
          disabled={isEdit || refreshLoading || !!history}
        />
        <RefreshButton onPress={refresh} loading={refreshLoading} disabled={isEdit || amlLoading || !!history} />
      </UtilRow>

      <SC.Content>
        <DetailNav parentTitle={t('employee_list')} childTitle={t('employee_info')} onPressParent={onClose} />
        {isEdit ? (
          <EmployeeDetailEdit
            employeeKey={detail.employeeKey}
            onClose={() => {
              setIsEdit(false)
            }}
          />
        ) : (
          <EmployeeDetailView detail={history || detail} />
        )}
      </SC.Content>
    </>
  )
}

const SC = {
  Content: styled(View)`
    padding-left: 2rem;
    padding-right: 2rem;
  `
}
